/**
 * Resource Card Component
 *
 * Displays a game's Kirby resource state:
 * - Gateway status
 * - TM stack status
 * - SES ingester status
 * - Manual cleanup controls
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography,
  Alert,
  Switch,
  FormControlLabel,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@truefit/http-utils';

import StatusBadge from '@/shared/components/StatusBadge';
import GameHeaderRow from '@/shared/components/GameHeaderRow';
import { paletteHue } from '@/shared/utils/paletteHues';

import {
  canLaunchService,
  canReset,
  canStartStack,
  gatewayFlowState,
  serviceStatus,
  SERVICE_STATUS_TONES,
  STATE_COLORS,
  STATE_IN_FLIGHT,
} from '../resourceStates';
import { useCanEditResources } from '../hooks/useCanEditResources';
import { useKirbyMode } from '../hooks/useKirbyMode';
import { useResourceStacksQuery } from '../hooks/useResourceStacksQuery';
import GatewaySignalFlow from './GatewaySignalFlow';
import { ResourceStatusLegendButton } from './ResourceStatusLegend';

interface ResourceCardProps {
  game: any;
}

/**
 * What a game the API has no resource row for looks like. Nothing launched, so
 * nothing to clean up — the same shape the card would get back once the row
 * exists at `not_started`, which is why the render path below needs no branch
 * for it.
 */
const NOT_STARTED_STATE = {
  state: 'not_started',
  manualCleanupMode: false,
  manualCleanupPending: false,
};

export function ResourceCard({ game }: ResourceCardProps) {
  const queryClient = useQueryClient();
  const canEdit = useCanEditResources();
  // Under `ses-only` this environment never launches a gateway or a TM set, so
  // their tiles, their launch buttons and the signal-flow diagram between them
  // would all describe services that cannot exist here.
  const { mode: kirbyMode } = useKirbyMode();
  const sesOnly = kirbyMode === 'ses-only';
  // Nothing runs here, so nothing can be launched, cleaned up or reset — the
  // scheduler's handlers return before touching a resource, and reset would
  // leave its pending flag raised. The controls stay visible but inert: the
  // recorded status is still worth reading, the actions are just not offered.
  // This is also the reading while the mode is in flight, which is the safe
  // direction — a button that arrives dead and wakes up beats one that takes a
  // click the API then refuses.
  const kirbyOff = kirbyMode === 'off';
  const [openCleanupDialog, setOpenCleanupDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [manualCleanupModeEnabled, setManualCleanupModeEnabled] = useState(false);
  const [showFlow, setShowFlow] = useState(false);
  // Which feed the shutdown dialog is asking about, or null when it is closed.
  // The feed name doubles as the open flag because there is nothing to confirm
  // without one.
  const [stopFeed, setStopFeed] = useState<string | null>(null);

  // Fetch resource state
  const { data: resourceState, isLoading, error } = useQuery({
    queryKey: ['resources', game.id],
    queryFn: async () => {
      try {
        const response = await get(`/v1.0/games/${game.uniqueId}/resources`);
        return response.data;
      } catch (err) {
        // 425: the game is real but has no resource row yet. Answering with the
        // not-started state rather than rethrowing is what keeps the card on
        // screen — an unprovisioned game is precisely the one you want the
        // launch buttons for, and the error alert hides them.
        //
        // `status`, not `response.status`: configureHttp's response interceptor
        // rewrites every axios error into a plain Error, flattening the code
        // onto `status` (see util/errorHandlers.ts).
        if ((err as { status?: number }).status === 425) {
          return NOT_STARTED_STATE;
        }
        throw err;
      }
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });

  // Only while the panel is open. The page renders one card per game of the
  // day, and this is a second poll per card against an uncached endpoint.
  const {
    data: flow,
    isLoading: flowLoading,
    error: flowError,
  } = useResourceStacksQuery(game.uniqueId, showFlow);

  // One reading per service, shared by the tile below and by the launch button
  // for it. The tiles used to each derive their own answer off a different
  // field, which is how the card could show a green gateway, a ready TM set and
  // a failed game all at once.
  const gatewayStatus = serviceStatus(resourceState, 'gateway');
  const tmStatus = serviceStatus(resourceState, 'tm');
  const sesStatus = serviceStatus(resourceState, 'ses');

  // Set manual cleanup mode mutation
  const setManualCleanupMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await post(`/v1.0/games/${game.uniqueId}/resources/manual-cleanup-mode`, {
        enabled,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', game.id] });
      setManualCleanupModeEnabled(resourceState?.manualCleanupMode);
    },
  });

  // On-demand launches: the same scheduler jobs the cron fires in production,
  // just triggered by hand for lab use.
  const launchMutation = useMutation({
    mutationFn: async (service: 'gateway' | 'tm' | 'ses') => {
      const response = await post(
        `/v1.0/games/${game.uniqueId}/resources/launch-${service}`,
        service === 'tm' ? { pgm: true } : {},
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', game.id] });
    },
  });

  // Trigger cleanup mutation
  const triggerCleanupMutation = useMutation({
    mutationFn: async () => {
      const response = await post(`/v1.0/games/${game.uniqueId}/resources/trigger-cleanup`, {
        reason: 'manual-request',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', game.id] });
      setOpenCleanupDialog(false);
    },
  });

  // One of the two per-feed actions on this card, with start below. Everything
  // else here is game-wide: cleanup and reset take the gateway, the ingester and
  // every stack together, because kirby/cleanup has no notion of a camera. This
  // stops one stack and leaves the rest of the broadcast on air.
  //
  // Synchronous on the API side rather than queued, so a refusal is reported
  // here instead of disappearing into a job nobody watches.
  const stopStackMutation = useMutation({
    mutationFn: async (feed: string) => {
      const response = await post(
        `/v1.0/games/${game.uniqueId}/resources/stop-stack`,
        { feed },
      );
      return response.data;
    },
    onSuccess: () => {
      // The flow panel's query, not the card's: this changes what TM is
      // running, and that is what /resources/stacks reports. The lane keeps
      // reading `running` until kirby/tm-poll next observes the stack, so it
      // goes quiet a poll later rather than on this invalidation.
      queryClient.invalidateQueries({
        queryKey: ['resourceStacks', game.uniqueId],
      });
      setStopFeed(null);
    },
  });

  // Starts one stopped feed again from the config TM kept for it. No
  // confirmation, unlike stop: nothing goes off air.
  const startStackMutation = useMutation({
    mutationFn: async (feed: string) => {
      const response = await post(
        `/v1.0/games/${game.uniqueId}/resources/start-stack`,
        { feed },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['resourceStacks', game.uniqueId],
      });
    },
  });

  // Reset: tear down, then clear the row back to not_started so the game can be
  // deployed again. Asynchronous — the endpoint queues kirby/cleanup and
  // answers 202, so the card's 5s poll is what actually shows the result.
  const resetMutation = useMutation({
    mutationFn: async () => {
      const response = await post(`/v1.0/games/${game.uniqueId}/resources/reset`, {});
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', game.id] });
      setOpenResetDialog(false);
    },
  });

  if (isLoading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', justifyContent: 'center', minHeight: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert severity="error">Failed to load resource state</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Game envelope — shared with the WSC Streams page. The resource state
          rides in the right-hand cluster next to the game time. */}
      <GameHeaderRow
        game={game}
        endAdornment={
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.25 }}>
            <StatusBadge
              label={resourceState?.state?.replace(/_/g, ' ') || 'unknown'}
              color={paletteHue(STATE_COLORS[resourceState?.state])}
              pulse={STATE_IN_FLIGHT.has(resourceState?.state)}
            />
            <ResourceStatusLegendButton />
          </Stack>
        }
      />

      <CardContent>
        {/* Error alert */}
        {resourceState?.failureReason && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {resourceState.failureReason}
          </Alert>
        )}

        {/* Services status. Under ses-only only the ingester exists, so its tile
            takes the whole row rather than sitting in a third of one beside two
            permanent "Not launched" placeholders. */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Gateway */}
          {!sesOnly && (
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Gateway
                </Typography>
                {resourceState?.gateway ? (
                  <Stack spacing={0.5}>
                    <Typography variant="caption">{resourceState.gateway.instanceId}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {resourceState.gateway.ipAddress}
                    </Typography>
                    {/* Labelled in EC2's words — the same ones the signal flow
                        prints for this instance — so the tile and the diagram
                        cannot appear to disagree about one gateway. */}
                    <StatusBadge
                      label={gatewayFlowState(resourceState)}
                      color={paletteHue(SERVICE_STATUS_TONES[gatewayStatus])}
                      pulse={gatewayStatus === 'launching'}
                    />
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Not launched
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* TM */}
          {!sesOnly && (
            <Grid item xs={12} sm={4}>
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  TM Stack
                </Typography>
                {resourceState?.tm ? (
                  <Stack spacing={0.5}>
                    <Typography variant="caption">{resourceState.tm.tmSetId}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {resourceState.tm.stacks?.length || 0} stacks
                    </Typography>
                    {/* `tmReadyAt` is a milestone, not a state: it survives the
                        teardown that stops every stack. Reading it alone is what
                        left this tile saying READY while the flow showed four
                        finished stacks. */}
                    <StatusBadge
                      label={tmStatus.replace(/_/g, ' ')}
                      color={paletteHue(SERVICE_STATUS_TONES[tmStatus])}
                      pulse={tmStatus === 'launching'}
                    />
                  </Stack>
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Not launched
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* SES */}
          <Grid item xs={12} sm={sesOnly ? 12 : 4}>
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                SES Ingester
              </Typography>
              {resourceState?.ses ? (
                <Stack spacing={0.5}>
                  <Typography variant="caption">{resourceState.ses.deploymentName}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {resourceState.ses.podCount || 0} pods
                  </Typography>
                  <StatusBadge
                    label={sesStatus.replace(/_/g, ' ')}
                    color={paletteHue(SERVICE_STATUS_TONES[sesStatus])}
                    pulse={sesStatus === 'launching'}
                  />
                </Stack>
              ) : (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Not launched
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Signal flow. The same panel the Test lab draws — the live path is
            the pipeline the lab rehearses — so the gateway, the per-feed TM
            stacks and where each one writes are read the same way in both.

            Collapsed by default, and the query behind it is gated on that:
            open, it polls an uncached endpoint, and this card is rendered once
            per game on the schedule.

            Hidden entirely under ses-only: the diagram is the gateway-to-TM
            path, and neither end of it is launched in that mode. */}
        {!sesOnly && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              onClick={() => setShowFlow((open) => !open)}
              startIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: 'transform 200ms',
                    transform: showFlow ? 'rotate(0)' : 'rotate(-90deg)',
                  }}
                />
              }
            >
              Signal flow
            </Button>
            {/* unmountOnExit: a collapsed Collapse keeps its children mounted,
                and this page draws one card per game of the day. */}
            <Collapse in={showFlow} unmountOnExit>
              <Box sx={{ mt: 1.5 }}>
                {flowLoading ? (
                  <CircularProgress size={20} />
                ) : flowError ? (
                  <Alert severity="error">
                    {(flowError as Error).message}
                  </Alert>
                ) : (flow?.feeds?.length ?? 0) === 0 ? (
                  <Alert severity="info">
                    No feeds to draw: this game has no cameras configured, or none
                    of them appear in TM_PORT_MAPPING.
                  </Alert>
                ) : (
                  <GatewaySignalFlow
                    hostname={flow?.hostname ?? ''}
                    gatewayState={gatewayFlowState(resourceState)}
                    gatewayIpAddress={resourceState?.gateway?.ipAddress ?? null}
                    instanceId={resourceState?.gateway?.instanceId ?? null}
                    feeds={flow?.feeds ?? []}
                    stacks={flow?.tmStacks ?? []}
                    // Undefined rather than a disabled button for read-only
                    // users and dead environments, so the panel draws no
                    // control at all — the same choice the launch box makes by
                    // hiding itself rather than sitting there inert.
                    onStopStack={
                      canEdit && !kirbyOff
                        ? (feed) => {
                            // Clear a previous feed's failure first: the dialog
                            // renders this mutation's error, and a stale one
                            // would appear against a camera nobody has tried.
                            stopStackMutation.reset();
                            setStopFeed(feed);
                          }
                        : undefined
                    }
                    stoppingFeed={stopStackMutation.isPending ? stopFeed : null}
                    onStartStack={
                      canEdit && !kirbyOff && canStartStack(resourceState)
                        ? (feed) => startStackMutation.mutate(feed)
                        : undefined
                    }
                    startingFeed={
                      startStackMutation.isPending ? startStackMutation.variables : null
                    }
                  />
                )}
                {startStackMutation.isError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {(startStackMutation.error as Error).message}
                  </Alert>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* On-demand launches. Read-only users do not see this at all: three
            permanently dead buttons and a note about launch ordering say
            nothing to someone whose POST would come back 403. */}
        {canEdit && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Launch on demand
            </Typography>
            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {!sesOnly && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      kirbyOff ||
                      launchMutation.isPending ||
                      !canLaunchService(resourceState, 'gateway')
                    }
                    onClick={() => launchMutation.mutate('gateway')}
                  >
                    Gateway
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      kirbyOff ||
                      launchMutation.isPending ||
                      !canLaunchService(resourceState, 'tm')
                    }
                    onClick={() => launchMutation.mutate('tm')}
                  >
                    TM Stacks
                  </Button>
                </>
              )}
              <Button
                size="small"
                variant="outlined"
                disabled={
                  kirbyOff ||
                  launchMutation.isPending ||
                  !canLaunchService(resourceState, 'ses')
                }
                onClick={() => launchMutation.mutate('ses')}
              >
                SES Ingester
              </Button>

              {/* Reset sits with the launch buttons because it is what makes
                  them live again: every one of them refuses a service that is
                  already provisioned. Pushed to the right so it reads as the
                  opposite of the row, not another launch. */}
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<RestartAltIcon />}
                disabled={kirbyOff || resetMutation.isPending || !canReset(resourceState)}
                onClick={() => setOpenResetDialog(true)}
                sx={{ ml: 'auto' }}
              >
                Reset
              </Button>
            </Stack>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mt: 1 }}
            >
              {kirbyOff
                ? 'Kirby is disabled in this environment (KIRBY_MODE=off); nothing can be launched or reset here.'
                : sesOnly
                  ? 'This environment runs the SES ingester only; the gateway and TM stacks are not launched here.'
                  : 'Each step waits for the previous one, so the gateway has to be up before TM connects. The lab normally stops after TM.'}
            </Typography>
            {launchMutation.isError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {(launchMutation.error as Error).message}
              </Alert>
            )}
            {resetMutation.isError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {(resetMutation.error as Error).message}
              </Alert>
            )}
          </Box>
        )}

        {/* Manual cleanup controls */}
        <Box sx={{ p: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={resourceState?.manualCleanupMode || false}
                onChange={(e) => setManualCleanupMutation.mutate(e.target.checked)}
                disabled={!canEdit || kirbyOff || setManualCleanupMutation.isPending}
              />
            }
            label="Manual cleanup mode"
          />
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
            When enabled, resources will not auto-cleanup after the game ends
          </Typography>

          {resourceState?.manualCleanupMode && (
            <Stack sx={{ mt: 2, gap: 1 }}>
              {resourceState?.manualCleanupPending ? (
                <Alert severity="info" size="small">
                  Cleanup in progress...
                </Alert>
              ) : canEdit ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    disabled={kirbyOff}
                    onClick={() => setOpenCleanupDialog(true)}
                  >
                    Trigger Cleanup Now
                  </Button>
                </Box>
              ) : null}
            </Stack>
          )}
        </Box>
      </CardContent>

      {/* Cleanup confirmation dialog */}
      <Dialog open={openCleanupDialog} onClose={() => setOpenCleanupDialog(false)}>
        <DialogTitle>Confirm Cleanup</DialogTitle>
        <DialogContent>
          <Typography>
            This will shut down the SIP radius gateway, TM stacks, and SES ingester for this game.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCleanupDialog(false)}>Cancel</Button>
          <Button
            onClick={() => triggerCleanupMutation.mutate()}
            color="error"
            variant="contained"
            disabled={triggerCleanupMutation.isPending}
          >
            {triggerCleanupMutation.isPending ? 'Triggering...' : 'Trigger Cleanup'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Per-feed shutdown confirmation. Confirmed for the same reason cleanup
          and reset are: on a live game this takes one camera off air. */}
      <Dialog open={stopFeed !== null} onClose={() => setStopFeed(null)}>
        <DialogTitle>Shut down {stopFeed}?</DialogTitle>
        <DialogContent>
          <Typography>
            This stops the TiledMedia stack for <strong>{stopFeed}</strong> only.
            The gateway, the SES ingester and every other camera keep running.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            The feed's config is kept, so it can be started again from the
            signal flow.
          </Typography>
          {stopStackMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(stopStackMutation.error as Error).message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStopFeed(null)}>Cancel</Button>
          <Button
            onClick={() => stopFeed && stopStackMutation.mutate(stopFeed)}
            color="error"
            variant="contained"
            disabled={stopStackMutation.isPending}
          >
            {stopStackMutation.isPending ? 'Stopping...' : 'Shut down'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset confirmation dialog */}
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>
          <Typography>
            This tears down {sesOnly ? 'the SES ingester' : 'the SIP radius gateway, TM stacks, and SES ingester'} for
            this game, then clears its resource state back to not started so it
            can be deployed again.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Teardown runs first and takes a moment; the card updates on its next
            refresh.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)}>Cancel</Button>
          <Button
            onClick={() => resetMutation.mutate()}
            color="warning"
            variant="contained"
            disabled={resetMutation.isPending}
          >
            {resetMutation.isPending ? 'Resetting...' : 'Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
